import { Empty, Flex, Spin, Timeline } from 'antd'
import { FunctionComponent, ReactNode } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { useFetchInfiniteKolFootprintInfiniteQuery } from '@/api/workspace-api/directory-api'
import FootprintHistoryContent from '@/components/common/footprint-history-content'
import { InitialLoadingBlock } from '@/components/common/initial-loading-block'
import useInitFetchKolFootprint from '@/components/kol/common/kol-cooperation-detail-drawer/footprint-history/use-init-fetch-footprint'
import TabButton from '@/components/kol/common/kol-cooperation-detail-drawer/tab-button'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import { useSelector } from '@/hooks/use-selector'
import { I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  setActiveFootprintTabKey,
  setOpen,
} from '@/store/kol-cooperation-detail-drawer'
import {
  FetchKolFootprintResourceType,
  Footprint,
} from '@/types/schema/directory/kol-management'
import { toVoidFunction } from '@/utils/to-void-function'

interface FootprintProps {
  kolId: string
}

const tabI18nMap: Record<FetchKolFootprintResourceType, I18nId> = {
  all: 'kol:audience_analysis_gender_all',
  'collection-folder': 'directory:collection_folder',
  campaign: 'irm:navbar_campaign',
  'deep-report': 'irm:navbar_deep_report',
  other: 'kol:edit_field_gender_other',
}

const FootprintHistory: FunctionComponent<FootprintProps> = ({ kolId }) => {
  const dispatch = useDispatch()
  const { formatMessage, formatDate } = useIntl()
  const { kolInfo } = useKolInfo(kolId)

  const activeFootprintTabKey = useSelector(
    (state) => state.kolCooperationDetailDrawer.activeFootprintTabKey,
  )

  const { resourceTypeTotal, isLoading: initLoading } =
    useInitFetchKolFootprint(kolId)

  const {
    data: dataFootprintResponse,
    isLoading: isLoadingFootprint,
    isFetching: isFetchingFootprint,
    isError,
    hasNextPage,
    fetchNextPage,
  } = useFetchInfiniteKolFootprintInfiniteQuery({
    kolId,
    footprint_resource_type:
      activeFootprintTabKey === 'all' ? undefined : activeFootprintTabKey,
  })

  const footprints =
    dataFootprintResponse?.pages.map((page) => page.data.footprints).flat() ??
    []

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingFootprint,
    hasNextPage,
    onLoadMore: toVoidFunction(fetchNextPage),
    disabled: isError,
  })

  if (initLoading) {
    return <InitialLoadingBlock />
  }

  return (
    <Wrapper>
      <Flex>
        {Object.entries(resourceTypeTotal).map(
          ([key, count]: [FetchKolFootprintResourceType, number]) => (
            <TabButton
              active={key === activeFootprintTabKey}
              content={{
                text: formatMessage({ id: tabI18nMap[key] }),
                count,
              }}
              key={key}
              onClick={() => {
                dispatch(setActiveFootprintTabKey(key))
              }}
            />
          ),
        )}
      </Flex>
      {((): ReactNode => {
        if (isLoadingFootprint) {
          return <Spin />
        }
        if (footprints.length === 0) {
          return (
            <Flex>
              <StyledEmpty
                description={formatMessage({
                  id: 'autoreport:chart_label_empty_data',
                })}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                {formatMessage({
                  id: 'directory:footprint_empty_hint',
                })}
              </StyledEmpty>
            </Flex>
          )
        }
        return (
          <Timeline>
            {footprints.map((footprint: Footprint) => (
              <TimelineItem key={footprint.id}>
                <FootprintHistoryContent
                  {...footprint}
                  kolName={kolInfo?.name}
                  onLinkClick={(): void => {
                    dispatch(setOpen(false))
                  }}
                />
                <TimelineItemContent>
                  <span>
                    {formatDate(footprint.created_at, 'dateTimeLong')}
                  </span>
                  <span>{footprint.user_name}</span>
                </TimelineItemContent>
              </TimelineItem>
            ))}
          </Timeline>
        )
      })()}
      {!isFetchingFootprint && hasNextPage && (
        <Flex justify='center' ref={sentryRef}>
          <Spin />
        </Flex>
      )}
    </Wrapper>
  )
}

export default FootprintHistory

const StyledEmpty = styled(Empty)`
  width: 100%;
  display: flex;
  flex-direction: column;
  .ant-empty-footer {
    color: ${({ theme }): string => theme.colors.text.secondary};
    font-size: 14px;
    line-height: 22px;
    margin-top: 24px;
  }
`

const TimelineItemContent = styled.div`
  display: flex;
  gap: 4px;
  color: ${({ theme }): string => theme.colors.text.tooltip};
`
const TimelineItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 14px;
  line-height: 1.4;
`

const Wrapper = styled.div`
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  .ant-timeline .ant-timeline-item-head-blue {
    border: ${({ theme }): string => theme.colors.brand.secondary} 3px solid;
    width: 12px;
    height: 12px;
    left: -2px;
  }
`
